import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { LIBRARY_SERVICE_METHOD, USER_PLAN_SERVICE_METHOD, USER_SERVICES_METHOD } from "services/commonService";
import { apiRequest } from "services/ServiceUtil";
import { SME_PAGE_SIZE } from "utils/CommonConstants";

const SLICE = "SME";

const initialState = {
  users: [],
  plans: [],
  planDetails: [],
  smeShowLoader: false,
  isListCreated: false,
  myLists: {
    records: [],
    count: 0
  },
  myListDetails: {
    id: null,
    mappings: [],
    name: "",
    createdDate: "",
    planAssigneeMap: [],
    assignedPlanCount: 0,
    lowEngagementCount: 0,
    highEngagementCount: 0,
    mediumEngagementCount: 0,
    overDueCardPlansCount: 0,
    innCodeGroupMappingCount: 0,
    unAssignedCardPlansCount: 0,
    meta: {
      page: { total: 0 },
      filters: []
    }
  }
};

export const fetchMyLists = createAsyncThunk(`${SLICE}/fetchMyLists`, (data: any, thunkApi?: any) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.getInncodeGroup, {}, thunkApi, [], {
    page: data?.page ?? 0,
    size: data?.size ?? SME_PAGE_SIZE,
    ...(data?.searchText && { searchText: data.searchText })
  })
);

export const fetchMyListDetails = createAsyncThunk(`${SLICE}/fetchMyListDetails`, (data: any, thunkApi: any) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.getInncodeGroup, {}, thunkApi, [data?.id], {
    view: data?.view,
    page: data?.page || 0,
    engagement: data?.engagement || "",
    inncode: data.innCode || "",
    size: data?.size || SME_PAGE_SIZE
  })
);

export const getUserDetails = createAsyncThunk(`${SLICE}/getUserDetails`, async (hltIds: string[], thunkApi?: any) =>
  apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], {
    page: 0,
    size: hltIds.length,
    hltIds: hltIds
  })
);

export const updateInncodeUploadList = createAsyncThunk(`${SLICE}/updateInncodeUploadList`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.postInncodeGroup, data, thunkApi)
);

export const uploadMyList = createAsyncThunk(`${SLICE}/uploadMyList`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.uploadImportList, data, thunkApi)
);

export const updateSCList = createAsyncThunk(`${SLICE}/updateSCList`, (data: any, thunkApi) =>
  apiRequest(USER_PLAN_SERVICE_METHOD.patchInncodeGroup, data.payload, thunkApi, [data?.groupId])
);

export const getUserDetail = createAsyncThunk(`${SLICE}/getUserDetail`, (mappedUsers: string[], thunkApi?: any) => {
  const hltIds = mappedUsers.filter((val, index, arr) => arr.indexOf(val) === index);
  apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], {
    page: 0,
    size: hltIds.length,
    hltIds: hltIds
  });
});

export const fetchPlans = createAsyncThunk(`${SLICE}/fetchPlans`, (data: any, thunkApi?: any) =>
  apiRequest(
    LIBRARY_SERVICE_METHOD.postPlans,
    {
      ...data
    },
    thunkApi
  )
);

export const SMESlice = createSlice({
  name: SLICE,
  initialState,
  reducers: {
    setUsers: (state, action) => {
      // @ts-ignore
      state.users = [...state.users, ...action.payload];
    },

    setPlans: (state, action) => {
      state.plans = action.payload.plans;
    },
    resetMyList: (state) => {
      state.myLists = {
        records: [],
        count: 0
      };
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchMyLists.pending, (state) => {
        state.smeShowLoader = true;
      })
      .addCase(fetchMyLists.fulfilled, (state, action) => {
        state.myLists.records =
          !action.meta.arg?.page || action.meta.arg?.page === 0
            ? action.payload?.innCodeGroups || []
            : [...state.myLists.records, ...action.payload.innCodeGroups];
        state.myLists.count = action.payload?.page?.total || 0;
        state.smeShowLoader = false;
      })
      .addCase(fetchMyLists.rejected, (state) => {
        state.smeShowLoader = false;
        state.myLists.count = 0;
        state.myListDetails = {
          id: null,
          mappings: [],
          name: "",
          createdDate: "",
          planAssigneeMap: [],
          assignedPlanCount: 0,
          lowEngagementCount: 0,
          highEngagementCount: 0,
          mediumEngagementCount: 0,
          overDueCardPlansCount: 0,
          innCodeGroupMappingCount: 0,
          unAssignedCardPlansCount: 0,
          meta: {
            page: { total: 0 },
            filters: []
          }
        };
      })
      .addCase(fetchMyListDetails.pending, (state) => {
        state.smeShowLoader = true;
      })
      .addCase(fetchMyListDetails.fulfilled, (state, action) => {
        state.myListDetails = action.meta.arg?.page
          ? { ...state.myListDetails, mappings: [...state.myListDetails.mappings, ...action.payload.mappings] }
          : action.payload;
        state.smeShowLoader = false;
        if (action.payload?.mappings) {
          const mappedUsers = action.payload?.mappings.filter((el: any) => el.hltId).map((el: any) => el.hltId);
          if (mappedUsers.length) {
            getUserDetail(mappedUsers);
          }
        }
        if (action.payload?.planAssigneeMap) {
          const mappedIds = action.payload?.planAssigneeMap.map((el: any) => el.cmsPlanId);
          if (mappedIds.length) {
            const props = {
              filter: {
                ids: mappedIds
              },
              pagination: {
                page: 1,
                size: mappedIds.length
              },
              isSortingDisabled: true
            };
            fetchPlans(props);
          }
        }
      })
      .addCase(fetchMyListDetails.rejected, (state) => {
        state.smeShowLoader = false;
        state.myListDetails = { ...state.myListDetails, mappings: [] };
      })
      .addCase(getUserDetail.fulfilled, (state: any, action: any) => {
        state.users = [...state.users, ...action.payload?.users];
      })
      .addCase(fetchPlans.fulfilled, (state: any, action: any) => {
        state.plans = action.payload.plans;
      })
      .addCase(uploadMyList.pending, (state) => {
        state.smeShowLoader = true;
        state.isListCreated = false;
      })
      .addCase(uploadMyList.fulfilled, (state) => {
        state.smeShowLoader = false;
        state.isListCreated = true;
      })
      .addCase(uploadMyList.rejected, (state, action: any) => {
        state.smeShowLoader = false;
        state.isListCreated = false;
      })
      .addCase(updateInncodeUploadList.pending, (state) => {
        state.smeShowLoader = true;
        state.isListCreated = false;
      })
      .addCase(updateInncodeUploadList.fulfilled, (state) => {
        state.smeShowLoader = false;
        state.isListCreated = true;
      })
      .addCase(updateInncodeUploadList.rejected, (state, action: any) => {
        state.smeShowLoader = false;
        state.isListCreated = false;
      })
      .addCase(getUserDetails.pending, (state) => {
        state.smeShowLoader = true;
      })
      .addCase(getUserDetails.fulfilled, (state, action: any) => {
        state.smeShowLoader = false;
      })
      .addCase(getUserDetails.rejected, (state) => {
        state.smeShowLoader = false;
      })
      .addCase(updateSCList.pending, (state) => {
        state.smeShowLoader = true;
      })
      .addCase(updateSCList.fulfilled, (state, action: any) => {
        state.smeShowLoader = false;
      })
      .addCase(updateSCList.rejected, (state) => {
        state.smeShowLoader = false;
      });
  }
});

export const { setUsers, setPlans, resetMyList } = SMESlice.actions;

export default SMESlice.reducer;
